import { checkIsApplePayExpressCheckout, checkIsExpressCheckout } from '@JohnLewisPartnership/checkout-psp-ui';

// Checkout Forms
import {
  setSubmittingTrue,
  setSubmittingFalse,
  submitForm,
  touchForm,
  resetForm,
} from 'checkout-forms/redux/actions/form/formActions';
import validate from 'checkout-forms/form/validation/validate';
import { ADDRESS_SEARCH_FORM_ID } from 'checkout-forms/form/configs/addressSearch';
import { ADDRESS_SEARCH_INPUT_ID } from 'checkout-forms/form/fields/addressSearchInput';

// Config
import { createRecordImpression } from '../app/appActions';
import { goBack, navigateTo, navigateReplace } from '../navigation/navigationActions';
import {
  ANALYTICS_PAYMENT_CARD_DELETE_OPEN,
  ANALYTICS_PAYMENT_OPTION_SELECTED,
  ANALYTICS_PLACE_ORDER_AND_PAY_CLICK,
  ANALYTICS_PLACE_ORDER_AND_PAY_CLICK_START,
  CARD_FORM_VALIDATION_STATUS,
  CARD_PAYMENT,
  CARD_TYPE_MISSING,
  CHANGE_PAYMENT_DETAILS,
  CLOSE_POS_CREDIT_GET_READY_MODAL,
  GIFT_OPTIONS_CHANGE_OPTION,
  GIFT_OPTIONS_MODAL_CLOSE,
  GIFT_OPTIONS_MODAL_OPEN,
  HIDE_POS_CREDIT_BASKET_AMEND_MODAL,
  MOUNT_POS_CREDIT_ADDRESS_FORM,
  PAYMENT_CARD_DELETE,
  SET_PAYMENT_PROCESSING,
  SET_PAYMENT_TYPE,
  SET_SELECTED_SAVED_CARD,
  SHOW_POS_CREDIT_BASKET_AMEND_MODAL,
  SHOW_POS_CREDIT_GET_READY_MODAL,
  TRANSACTION_STATUS_UPDATE,
  GET_PAYMENTS,
  POST_PAYMENTS,
  GET_GIFTING_PAYMENT_DETAILS,
} from '@constants/actionConstants';
import { URL_CUSTOMER_PAYMENT_CARD_DELETE, URL_PAYMENTS, URL_GIFT_TOKENS } from '@constants/endpointConstants';
import errorCodeConstants from '@constants/errorCodeConstants';
import featureConstants from '@constants/featureConstants';
import monetateConstants from '@constants/monetateConstants';
import paymentTypeConstants from '@constants/paymentTypeConstants';
import routeConstants from '@constants/routeConstants';
import { putUpdatedAddress } from '@redux/actions/addressBook/addressBookActions';
import { triggerAnalyticsEvent } from '@redux/actions/analytics/analyticsAction';
import { setCutOffPassedModalVisibility } from '@redux/actions/click-and-collect/clickAndCollectActions';
import {
  isValid,
  processForms,
  handleSubmitFormValidationErrorAnalytics,
  resetAllOtherForms,
} from '@redux/actions/form/formActions';
import { applePayPaymentInit } from '@redux/actions/payment/applePayActions';
import { submitAndHandleCardPayment } from '@redux/actions/payment/cardPaymentActions';
import determineSelectedPaymentType from '@redux/actions/payment/selectedPaymentTypeActions';
import { submitOrderAndRedirect, resetSubmitOrderError } from '@redux/actions/payment/submitOrderActions';
import { fetchPaymentWallet } from '@redux/actions/user/userActions';
import { isSimplePhoneNumberFeatureActive } from '@redux/reducers/config/configSelector';
import { getRouterLocationPathname } from '@redux/reducers/router/routerSelector';
import { getRegisteredFormFieldNames, isFormMounted, isZeroAmount, scrollToElement } from '@utils';
import getBillingDetailsFormConfig, {
  BILLING_ADDRESS_FORM_ID,
  RESIDENTIAL_ADDRESS_FORM_ID,
} from '@utils/form/configs/billingAddress';
import { CARD_DETAILS_FORM_ID } from '@utils/form/configs/cardDetails';
import { getBillingAddress } from '@utils/address/addressHelpers';
import { dispatchMonetateEvent, shouldTriggerBarclaysMonetateEvent } from '@utils/analytics/trigger/monetateTrigger';
import { isClickAndCollectChoice, isDeliveryChoice } from '@utils/delivery/deliveryHelper';
import { PROMO_CODE_FORM_ID, PROMO_CODE } from '@utils/form/configs/promoCode';
import { SAVE_AS_DEFAULT_FORM_ID } from '@utils/form/configs/saveAsDefault';
import { SAVED_CARD_DETAILS_FORM_ID } from '@utils/form/configs/savedCardDetails';
import { sendNewRelicCustomEvent } from '@utils/logging/logging-utils';
import logUnsupportedCardType from '@utils/payment/logUnsupportedCardType';
import isSavedPaymentCard from '@utils/payment/isSavedPaymentCard';

export const recordStripeCheckoutEnabledImpressionId = createRecordImpression(featureConstants.STRIPE_CHECKOUT_ENABLED);
export const recordStripeCheckoutEnabledObservationImpressionId = createRecordImpression(
  featureConstants.STRIPE_CHECKOUT_ENABLED_OBSERVATION,
);

export const recordReduceSavedCardCvvSkipImpressionId = createRecordImpression(
  featureConstants.REDUCE_SAVED_CARD_CVV_SKIP,
);

export const recordKlarnaPayIn30DaysImpressionId = createRecordImpression(featureConstants.KLARNA_PAY_IN_30_DAYS);

export const recordConciseCardSecurityMessageImpressionId = createRecordImpression(
  featureConstants.CONCISE_CARD_SECURITY_MESSAGE,
);

export const showPaymentCardDeleteModal = () => (dispatch) => {
  dispatch(triggerAnalyticsEvent(ANALYTICS_PAYMENT_CARD_DELETE_OPEN));
  dispatch(navigateTo(routeConstants.PAYMENT_CARD_DELETE));
};

export const deletePaymentCard = (cardId, last4Digits) => async (dispatch, getState) => {
  const isPartnerDiscountEnabled = getState().user?.partnerDiscountEnabled ?? false;

  const response = await dispatch({
    type: PAYMENT_CARD_DELETE,
    request: (client) =>
      client({
        path: URL_CUSTOMER_PAYMENT_CARD_DELETE(cardId),
        config: {
          method: 'DELETE',
        },
      }),
    isPartnerDiscountEnabled,
    last4Digits,
  });

  const errorCode = response?.error?.code;
  if (errorCode === errorCodeConstants.CLIENT_PAYMENT_CARD_NOT_DELETED) {
    await dispatch(fetchPaymentWallet());
  }

  dispatch(determineSelectedPaymentType());
  dispatch(navigateReplace(routeConstants.PAYMENT));
};

export const showBillingAddressBookModal = () => (dispatch) => {
  dispatch(navigateTo(routeConstants.PAYMENT_ADDRESS_BOOK));
};

const PAYMENT_TYPE_TO_MONETATE_EVENT = {
  [paymentTypeConstants.KLARNA]: 'checkout_klarna_selected',
};

export const setSelectedPaymentType =
  ({ paymentType = '', savedPaymentCards = [], shouldSetBillingAddress = true, posCreditType = '' } = {}) =>
  async (dispatch, getState) => {
    await dispatch({
      type: SET_PAYMENT_TYPE,
      paymentType,
      savedPaymentCards,
      shouldSetBillingAddress,
      posCreditType,
    });

    const eventName = PAYMENT_TYPE_TO_MONETATE_EVENT[paymentType || ''];

    if (eventName) {
      dispatchMonetateEvent({ eventName });
    }

    const payOptionsAnalytics = getState()?.payment?.payOptionsAnalytics ?? {};
    return dispatch(
      triggerAnalyticsEvent(ANALYTICS_PAYMENT_OPTION_SELECTED, {
        payOptionsAnalytics,
      }),
    );
  };

export const openGiftOptionsModal = () => async (dispatch) => {
  await dispatch({
    type: GIFT_OPTIONS_MODAL_OPEN,
  });

  dispatch(navigateTo(routeConstants.PAYMENT_GIFT_OPTIONS));
};

export const closeGiftOptionsModal = () => async (dispatch) => {
  const currentPath = getRouterLocationPathname();

  await dispatch({
    type: GIFT_OPTIONS_MODAL_CLOSE,
  });

  if (currentPath === routeConstants.PAYMENT_GIFT_OPTIONS) {
    dispatch(goBack());
  }
};

export const changeGiftOption = (contentId) => ({
  type: GIFT_OPTIONS_CHANGE_OPTION,
  contentId,
});

export const setPaymentProcessing = (value) => ({
  type: `${SET_PAYMENT_PROCESSING}.${value ? 'TRUE' : 'FALSE'}`,
});

export const setCardFormValidationStatus = (isCardFormValid) => ({
  type: CARD_FORM_VALIDATION_STATUS,
  isCardFormValid,
});

export const changePaymentDetails = () => ({
  type: CHANGE_PAYMENT_DETAILS,
});

export const triggerPlaceOrderAndPayStartAnalytics = (paymentType) => async (dispatch, getState) => {
  const { bff: { sessionId } = {} } = getState() ?? {};

  if (paymentType.includes('gpay')) {
    sendNewRelicCustomEvent('googlePayStarted', { sessionId });
  } else if (paymentType.includes('paypal')) {
    sendNewRelicCustomEvent('payPalStarted', { sessionId });
  } else if (paymentType.includes('applepay')) {
    sendNewRelicCustomEvent('applePayStarted', { sessionId });
  }

  dispatch({
    type: ANALYTICS_PLACE_ORDER_AND_PAY_CLICK_START,
    paymentType,
  });
};

export const triggerPlaceOrderAndPayAnalytics = (paymentId) => async (dispatch, getState) => {
  // TODO dispatching with promocode can be removed after https://www.jlpit.com/jira/browse/MARV-5940 has been determined
  const promoCodeText = getState().form?.[PROMO_CODE_FORM_ID]?.values?.[PROMO_CODE.id];
  const dispatchValues = {
    type: ANALYTICS_PLACE_ORDER_AND_PAY_CLICK,
    paymentId,
  };

  if (promoCodeText) {
    dispatch({
      ...dispatchValues,
      promoCodeText,
    });
  } else {
    dispatch(dispatchValues);
  }
};

export const triggerTransactionStatusAnalytics = (transactionStatus, args) => ({
  type: TRANSACTION_STATUS_UPDATE,
  transactionStatus,
  ...args,
});

export const pendingBrowserCheck = (id, actionUrl, formData) => ({
  type: `${CARD_PAYMENT}.SUCCESS`,
  result: {
    ocpPaymentId: id,
    threeDSMethodInfo: {
      threeDSMethodRequired: true,
      threeDSMethodFormId: '3DSMethodForm',
      threeDSMethodUrl: actionUrl,
      threeDSMethodDataFormData: formData,
    },
  },
});

export const pendingChallege = (actionUrl, formData) => ({
  type: `${CARD_PAYMENT}.SUCCESS`,
  result: {
    creditCard3DSecureInfo: {
      is3DSecureAvailable: true,
      creditCard3DSecureFormId: '3DSForm',
      creditCard3DSecureMerchantUrl: actionUrl,
      creditCard3DSecureFormData: formData,
    },
  },
});

export const handleInvalidSavedBillingAddress =
  ({ billingAddress = {} }) =>
  (dispatch, getState) => {
    const { user: { isSignedInWithData, addressBook = [] } = {} } = getState();

    if (isSignedInWithData) {
      const billingAddressInAddressBook = addressBook?.find((addressRecord) => addressRecord.id === billingAddress.id);

      if (billingAddressInAddressBook?.invalid) {
        return dispatch(putUpdatedAddress({ body: billingAddress }));
      }
    }

    return {};
  };

export const submitAndHandlePayment =
  ({ isStripe = false, type = '' }) =>
  async (dispatch, getState) => {
    const state = getState();

    const billingAddress = getBillingAddress(state);
    await dispatch(handleInvalidSavedBillingAddress({ billingAddress }));

    if (type === paymentTypeConstants.GIFT_OPTIONS) {
      await dispatch(triggerPlaceOrderAndPayAnalytics(null));
      return dispatch(submitOrderAndRedirect({ billingAddress, isStripe }));
    }

    if (isStripe) {
      const isExpressCheckout = checkIsExpressCheckout(type);
      const isApplePayExpress = checkIsApplePayExpressCheckout(type);
      const payload = {
        ...(isExpressCheckout ? {} : { billingAddress }),
        express: isApplePayExpress,
        isStripe,
      };

      return dispatch(submitOrderAndRedirect(payload));
    }

    const savedPaymentCards = state?.user?.savedPaymentCards;
    const isSavedPayment = isSavedPaymentCard(type);

    let cardDetails;
    let cardToBeStored = false;
    const { addressee: { lastName, firstName } = {} } = billingAddress;

    if (isSavedPayment) {
      const id = type.split(':')[1];
      const savedPaymentCard = savedPaymentCards?.find((paymentCard) => paymentCard.id === id);
      cardDetails = {
        ...state.form?.[SAVED_CARD_DETAILS_FORM_ID]?.values,
        cardNumber: savedPaymentCard.obfuscatedPan.substr(0, 6),
        cardType: savedPaymentCard.type,
        cardId: savedPaymentCard.id,
        expiryDate: `${savedPaymentCard.expirationYear}-${savedPaymentCard.expirationMonth}`,
        cardholderName: savedPaymentCard.nameOnCard,
      };
    } else if (type === paymentTypeConstants.KLARNA) {
      cardDetails = {
        ...state.payment.klarnaCardDetails,
        cardholderName: `${firstName} ${lastName}`,
      };
      cardToBeStored = false;
    } else {
      cardDetails = state.form?.[CARD_DETAILS_FORM_ID]?.values;
      cardToBeStored = state.form?.[SAVE_AS_DEFAULT_FORM_ID]?.values?.savePaymentForFuture ?? false;
    }

    const ocpBaseUrl = state.orderForm?.ocpIntegration?.baseUrl;
    return dispatch(
      submitAndHandleCardPayment({
        cardDetails,
        billingAddress,
        ocpBaseUrl,
        savedCardPayment: isSavedPayment,
        cardToBeStored,
      }),
    );
  };

export const setSavedPaymentFormsSubmittingTrue = () => (dispatch) => {
  dispatch(setSubmittingTrue(BILLING_ADDRESS_FORM_ID));
  dispatch(setSubmittingTrue(SAVED_CARD_DETAILS_FORM_ID));
};

export const setPaymentFormsSubmittingTrue = () => (dispatch) => {
  dispatch(setSubmittingTrue(BILLING_ADDRESS_FORM_ID));
  dispatch(setSubmittingTrue(CARD_DETAILS_FORM_ID));
};

export const setPaymentFormsSubmittingFalse = () => (dispatch) => {
  dispatch(setSubmittingFalse(BILLING_ADDRESS_FORM_ID));
  dispatch(setSubmittingFalse(CARD_DETAILS_FORM_ID));
};

export const setSavedPaymentFormsSubmittingFalse = () => (dispatch) => {
  dispatch(setSubmittingFalse(BILLING_ADDRESS_FORM_ID));
  dispatch(setSubmittingFalse(SAVED_CARD_DETAILS_FORM_ID));
};

export const clearPaymentPageErrors =
  (ignoreIds, keepSyncErrors = false) =>
  async (dispatch) => {
    await dispatch(resetSubmitOrderError());
    if (ignoreIds) {
      await dispatch(resetAllOtherForms(ignoreIds, keepSyncErrors));
    }
  };

const PAYMENT_FORMS = [
  CARD_DETAILS_FORM_ID,
  BILLING_ADDRESS_FORM_ID,
  SAVED_CARD_DETAILS_FORM_ID,
  RESIDENTIAL_ADDRESS_FORM_ID,
];

const validateInvalidAddressForm = () => async (dispatch) => {
  const processFormsErrors = await dispatch(processForms(PAYMENT_FORMS));

  const formSyncErrors = {
    ...processFormsErrors,
  };

  if (!isValid(formSyncErrors)) {
    await dispatch(handleSubmitFormValidationErrorAnalytics(formSyncErrors));
    return false;
  }

  return true;
};

const validateCardDetails = (type, isSavedPayment) => async (dispatch) => {
  let formId;

  if (isSavedPayment) {
    formId = SAVED_CARD_DETAILS_FORM_ID;
  } else {
    formId = CARD_DETAILS_FORM_ID;
  }

  const formSyncErrors = await dispatch(processForms(formId, PAYMENT_FORMS));

  if (!isValid(formSyncErrors)) {
    await dispatch(handleSubmitFormValidationErrorAnalytics(formSyncErrors));
    return false;
  }

  return true;
};

export const validateBillingAddress = () => async (dispatch, getState) => {
  const selectedPaymentType = getState().payment?.selectedPaymentType;
  const form =
    selectedPaymentType?.includes(paymentTypeConstants.POS_CREDIT) || false
      ? RESIDENTIAL_ADDRESS_FORM_ID
      : BILLING_ADDRESS_FORM_ID;

  const processFormsErrors = await dispatch(processForms(form, PAYMENT_FORMS));

  const formSyncErrors = {
    ...processFormsErrors,
  };

  if (!isValid(formSyncErrors)) {
    await dispatch(handleSubmitFormValidationErrorAnalytics(formSyncErrors));
    return false;
  }

  return true;
};

export const validatePostcodeSearch = () => async (dispatch, getState) => {
  const searchFormId = ADDRESS_SEARCH_FORM_ID;
  const searchForm = getState().form?.[searchFormId];

  if (isFormMounted(searchForm)) {
    const formSyncErrors = {};
    let loqateFieldOriginalValue;

    if (getState().form?.[searchFormId]?.values) {
      // MARV-6044 hack to provoke validation when:
      // a) customer has entered text in Loqate field and
      // b) has NOT selected an address

      // preserve the text the customer has enter
      loqateFieldOriginalValue = getState().form?.[searchFormId]?.values?.[ADDRESS_SEARCH_INPUT_ID];

      // reset the loqate form so it will be empty and invalid during submission
      dispatch(resetForm(searchFormId));
    }

    await dispatch(clearPaymentPageErrors([searchFormId]), false);
    // this will error in two scenarios
    // 1. if search fields is empty or invalid (syncError)
    // 2. if search field contains a valid postcode (via SubmissionError)
    // the user should always be prompted to complete the address search
    // before attempting to pay

    // ensure we have syncErrors
    const fieldNames = getRegisteredFormFieldNames(searchFormId, true);
    await dispatch(touchForm(searchFormId, fieldNames));
    // submit to trigger error
    dispatch(submitForm(searchFormId));
    const syncErrors = getState().form?.[searchFormId]?.syncErrors ?? {};

    formSyncErrors[searchFormId] = syncErrors;

    if (loqateFieldOriginalValue) {
      // MARV-6044 Hack part 2
      const loqateInput = document.querySelector(`input[name="${ADDRESS_SEARCH_INPUT_ID}"]`);

      if (loqateInput) {
        // Reinsert the customer's text into the loqate input WITHOUT triggering a change event
        // Can't use setFieldValue util as the change event causes validation error to vanish
        loqateInput.value = loqateFieldOriginalValue;

        // Focus in the input field to cause the address dropdown to appear
        // Timeout is necessary otherwise error message gains focus instead
        setTimeout(() => {
          loqateInput.focus();
        }, 0);
      }
    }

    if (!isValid(formSyncErrors)) {
      await dispatch(handleSubmitFormValidationErrorAnalytics(formSyncErrors));
      return false;
    }
  }

  return true;
};

export const validator = {
  validateInvalidAddressForm,
  validateBillingAddress,
  validateCardDetails,
  validatePostcodeSearch,
};

export const validateForms = {
  validateAll: (dispatch, deliveries, type, isDelivery, isClickAndCollect, isSavedPayment) =>
    new Promise(async (resolve) => {
      if (
        type === paymentTypeConstants.CREDIT_CARD ||
        isSavedPaymentCard(type) ||
        type === paymentTypeConstants.TOKENIZED_PAYMENT_CARD
      ) {
        const cardDetailsValid = await dispatch(validator.validateCardDetails(type, isSavedPayment));
        if (!cardDetailsValid) {
          return resolve(false);
        }
      }
      const invalidAddressFormValid = await dispatch(validator.validateInvalidAddressForm());
      if (!invalidAddressFormValid) {
        return resolve(false);
      }

      const billingDetailsValid = await dispatch(validator.validateBillingAddress(type));
      if (!billingDetailsValid) {
        return resolve(false);
      }

      const postcodeSearchValid = await dispatch(validator.validatePostcodeSearch());
      if (!postcodeSearchValid) {
        return resolve(false);
      }

      return resolve(true);
    }),
};

export const closePOSGetReadyModal = () => ({
  type: CLOSE_POS_CREDIT_GET_READY_MODAL,
});

export const showPOSGetReadyModal = () => ({
  type: SHOW_POS_CREDIT_GET_READY_MODAL,
});

export const showPOSCreditBasketAmendModal = (creditTypeGroup) => ({
  type: SHOW_POS_CREDIT_BASKET_AMEND_MODAL,
  basketAmendCreditTypeGroup: creditTypeGroup,
});

export const hidePOSCreditBasketAmendModal = () => ({
  type: HIDE_POS_CREDIT_BASKET_AMEND_MODAL,
});

export const validateSavedPosCreditAddress = () => async (dispatch, getState) => {
  const { form, user } = getState();

  const residentialAddressForm = form?.[RESIDENTIAL_ADDRESS_FORM_ID];

  if (!isFormMounted(residentialAddressForm)) {
    const simplePhoneNumberFeatureActive = isSimplePhoneNumberFeatureActive(getState());
    const address = user.selectedResidentialAddress || {};
    const result = validate(
      {
        ...address.address,
        ...address.addressee,
        phoneNumber: address.phoneNumber,
      },
      {
        config: getBillingDetailsFormConfig({
          actions: {},
          countryCode: address.address?.countryCode,
          configExtras: { isPosCredit: true },
          enableGBCounty: !!address.address?.countyStateOrProvince,
          isSimplePhoneNumberFeatureActive: simplePhoneNumberFeatureActive,
        }),
      },
    );

    // the pos credit billing address is invalid by pos-credit standards
    // so mount the form to display validation errors caught in validateAll
    if (Object.keys(result).length) {
      dispatch({ type: MOUNT_POS_CREDIT_ADDRESS_FORM });
    }
  }
};

export const selectEntirelyGiftTender = (state) => {
  const amounts = state.orderForm?.amounts;
  const totals = amounts?.paymentTotals;
  if (!amounts || !totals) return null;

  if (!isZeroAmount(amounts.outstandingBalanceRaw)) return null;

  const hasNonGiftTender = !isZeroAmount(totals.creditCardRaw) || !isZeroAmount(totals.payPalRaw);
  if (hasNonGiftTender) return null;

  const hasGiftCard = !isZeroAmount(totals.giftCardRaw);
  const hasGiftVoucher = !isZeroAmount(totals.giftVoucherRaw);

  if (hasGiftCard === hasGiftVoucher) return null;

  return hasGiftCard ? paymentTypeConstants.GIFT_CARD : paymentTypeConstants.GIFT_VOUCHER;
};

/**
 * Initiates submission of form data to the required payment APIs
 * If validation errors are present in the forms -> dispatches analytics error event and APIs are NOT called
 * If NO validation errors are present in the forms -> calls payment APIs and submits the order
 */
export const submitPayment =
  ({ isStripe = false } = {}) =>
  async (dispatch, getState) => {
    const state = getState();
    const cutOffPassed = state.clickAndCollect?.cutOffPassed;
    const showCutOffPassedModal = state.clickAndCollect?.showCutOffPassedModal;
    const selectedPaymentType = state.payment?.selectedPaymentType;
    const isPosCredit = selectedPaymentType?.includes(paymentTypeConstants.POS_CREDIT) || false;
    const isZeroOutstandingBalance = isZeroAmount(getState().orderForm?.amounts?.outstandingBalance);

    if (shouldTriggerBarclaysMonetateEvent({ isStripe, state })) {
      dispatchMonetateEvent({ eventName: monetateConstants.PAYMENT.BARCLAYS.PAYMENT_START });
    }

    if (isZeroOutstandingBalance) {
      const tenderType = selectEntirelyGiftTender(state);
      if (tenderType === paymentTypeConstants.GIFT_CARD) {
        dispatchMonetateEvent({ eventName: monetateConstants.PAYMENT.ENTIRELY_GIFT_CARD });
      } else if (tenderType === paymentTypeConstants.GIFT_VOUCHER) {
        dispatchMonetateEvent({ eventName: monetateConstants.PAYMENT.ENTIRELY_VOUCHER });
      }
    }

    const shouldRunPOSCBasketChecks = isPosCredit && !isZeroOutstandingBalance;

    if (shouldRunPOSCBasketChecks) {
      dispatch(validateSavedPosCreditAddress());
    }

    if (cutOffPassed && !showCutOffPassedModal) {
      dispatch(setCutOffPassedModalVisibility(true));
    } else {
      const selectedDeliveryChoiceId = getState().delivery?.selectedDeliveryChoiceId;
      const isDelivery = isDeliveryChoice(selectedDeliveryChoiceId);
      const isClickAndCollect = isClickAndCollectChoice(selectedDeliveryChoiceId);

      if (isClickAndCollect) {
        dispatch(setCutOffPassedModalVisibility(false));
      }

      const deliveries = getState().delivery?.deliveries ?? [];
      const type = isZeroOutstandingBalance ? paymentTypeConstants.GIFT_OPTIONS : selectedPaymentType;
      const isApplePayExpress = type === paymentTypeConstants.APPLE_PAY_EXPRESS;

      const isSavedPayment = isSavedPaymentCard(type);

      dispatch(setPaymentProcessing(true));

      if (!isApplePayExpress) {
        const validateFormsResult = await validateForms.validateAll(
          dispatch,
          deliveries,
          type,
          isDelivery,
          isClickAndCollect,
          isSavedPayment,
        );

        if (validateFormsResult && type.includes(paymentTypeConstants.POS_CREDIT)) {
          dispatch(showPOSGetReadyModal());
          return;
        }

        if (!validateFormsResult) {
          dispatch(setPaymentProcessing(false));
          dispatch(setCardFormValidationStatus(false));
          return;
        }
      }

      // @isCardFormValid redux state uses to inform view details toggle button to expand when form is invalid
      const isCardFormValidationFailed = state.payment?.isCardFormValid;
      if (!isCardFormValidationFailed) {
        dispatch(setCardFormValidationStatus(true));
      }

      // TODO see if we can reset selectedPaymentType when payment has been made entirely with gift cards/vouchers
      if (
        !isStripe &&
        selectedPaymentType === paymentTypeConstants.CREDIT_CARD &&
        !state.cardType?.name &&
        !isZeroOutstandingBalance
      ) {
        dispatch(logUnsupportedCardType());
        dispatch({
          type: CARD_TYPE_MISSING,
        });
        dispatch(setPaymentProcessing(false));

        return;
      }

      if (isSavedPayment) {
        dispatch(setSavedPaymentFormsSubmittingTrue());
      } else {
        dispatch(setPaymentFormsSubmittingTrue());
      }

      // Need to submit details form one last time...
      // in case user has edited anything since last details update
      let deliveryDetailsResponse;
      if (!deliveryDetailsResponse?.some((item) => item.hasOwnProperty('error'))) {
        // APPLE PAY PAYMENTS
        if (type === paymentTypeConstants.APPLE_PAY) {
          if (!isStripe) {
            dispatch(applePayPaymentInit());
            dispatch(setPaymentFormsSubmittingFalse());
            return;
          }
        }

        // OTHER PAYMENTS
        await dispatch(
          submitAndHandlePayment({
            isStripe,
            type,
          }),
        );

        if (isSavedPayment) {
          dispatch(setSavedPaymentFormsSubmittingFalse());
        } else {
          dispatch(setPaymentFormsSubmittingFalse());
        }

        const threeDSMethodRequired = getState().payment?.threeDSMethodInfo?.threeDSMethodRequired ?? false;
        if (!threeDSMethodRequired) {
          dispatch(setPaymentProcessing(false));
        }
      }
    }
  };

export const redirectFromGiftCardToPayment = () => async (dispatch) => {
  const targetPaymentType = paymentTypeConstants.CREDIT_CARD;

  dispatch(setSelectedPaymentType({ paymentType: targetPaymentType }));

  scrollToElement({
    id: targetPaymentType,
  });

  const cardEntry = document.querySelector(`input[value="${targetPaymentType}"]`);
  if (cardEntry) {
    setTimeout(() => {
      cardEntry.focus();
    }, 0);
  }
};

export const setSelectedSavedCard = (id) => ({
  type: SET_SELECTED_SAVED_CARD,
  id,
});

export const getPayments = () => ({
  type: GET_PAYMENTS,
  request: (client) => client({ path: URL_PAYMENTS, config: { method: 'GET' } }),
});

export const postPayments = () => ({
  type: POST_PAYMENTS,
  request: (client) => client({ path: URL_PAYMENTS, config: { method: 'POST' } }),
});

export const getGiftingPaymentDetails = () => ({
  type: GET_GIFTING_PAYMENT_DETAILS,
  request: (client) => client({ path: URL_GIFT_TOKENS, config: { method: 'POST' } }),
});
